import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah7iarp14a1h3mr6e6g";

export default function SubscribeForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过验证，请检查填写内容后重试。"
            : serverMsg || "提交失败了，请稍后再试一次。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络好像开小差了，请检查连接后重试。");
    }
  };

  return (
    <form
      id="guatian-subscribe-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h3 className="font-heading text-lg font-black text-foreground-950">订阅瓜田日报</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        留下你的称呼和邮箱，我们每天整理一封短邮件，把最值得看的事件和神评发给你。不刷屏，随时可退订。
      </p>

      <div className="mt-5 flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="subscribe-name" className="text-xs font-semibold text-foreground-700">
            你的称呼
          </label>
          <input
            id="subscribe-name"
            name="name"
            type="text"
            required
            maxLength={30}
            placeholder="例如：爱看评论的小林"
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="subscribe-email" className="text-xs font-semibold text-foreground-700">
            邮箱地址
          </label>
          <input
            id="subscribe-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="subscribe-interest" className="text-xs font-semibold text-foreground-700">
            最想看的栏目
          </label>
          <select
            id="subscribe-interest"
            name="interest"
            defaultValue="全部栏目"
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="全部栏目">全部栏目</option>
            <option value="娱乐圈">娱乐圈</option>
            <option value="社会热点">社会热点</option>
            <option value="日常生活">日常生活</option>
            <option value="科技数码">科技数码</option>
            <option value="体育赛事">体育赛事</option>
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="subscribe-message" className="text-xs font-semibold text-foreground-700">
            想对我们说的话（选填）
          </label>
          <textarea
            id="subscribe-message"
            name="message"
            rows={4}
            maxLength={500}
            placeholder="例如：想看更多评论区的暖心故事，最多 500 字。"
            className="resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          ></textarea>
        </div>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="comment-form-alt-field"
        />

        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-mail-send-line mr-2 text-base"></i>
          {status === "submitting" ? "正在提交…" : "订阅瓜田日报"}
        </button>

        {status === "success" && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
          >
            <i className="ri-checkbox-circle-line mt-0.5 text-base text-secondary-600"></i>
            订阅成功！我们已经记下你的邮箱，瓜田日报明天准时送达。
          </p>
        )}
        {status === "error" && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700"
          >
            <i className="ri-error-warning-line mt-0.5 text-base text-primary-600"></i>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}